import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Plus, Edit2, Trash2, Search, Mail, Phone, MapPin, History } from 'lucide-react';
import { toast } from 'sonner';

const emptyCustomer = {
  customer_name: '',
  contact_person: '',
  email: '',
  phone: '',
  billing_address: '',
  shipping_address: '',
  tax_id: '',
  customs_broker_email: '',
  notes: '',
};

const CustomersPage = () => {
  const { api } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [balances, setBalances] = useState({});
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortBal, setSortBal] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyCustomer);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([api().get('/customers'), api().get('/customers-balances').catch(() => ({ data: {} }))]);
      setCustomers(r.data || []);
      setBalances(b.data || {});
    } catch { toast.error('Failed to load customers'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyCustomer); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...emptyCustomer, ...c }); setDialogOpen(true); };

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api().put(`/customers/${editing.id}`, form);
        toast.success(`${form.customer_name} updated`);
      } else {
        await api().post('/customers', form);
        toast.success(`${form.customer_name} added`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Soft-delete "${c.customer_name}"? History will be preserved.`)) return;
    try {
      await api().delete(`/customers/${c.id}`);
      toast.success('Customer archived');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const bal = (c) => balances[c.id] || { open_balance: 0, overdue_amount: 0, max_days_overdue: 0 };
  const filtered = customers.filter(c => {
    if (overdueOnly && !(bal(c).overdue_amount > 0)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.customer_name || '').toLowerCase().includes(q)
      || (c.contact_person || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q);
  });
  if (sortBal) filtered.sort((a, b) => (bal(b).overdue_amount - bal(a).overdue_amount) || (bal(b).open_balance - bal(a).open_balance));
  const overdueCount = customers.filter(c => bal(c).overdue_amount > 0).length;

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Users className="w-7 h-7 text-slate-700" /> Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Master list of customers used by Sales Orders, Packing Slips, Bills of Lading, and Repair Orders.</p>
        </div>
        <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="customers-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Customer
        </Button>
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg">All Customers ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2 ml-auto">
              <Button size="sm" variant={overdueOnly ? 'default' : 'outline'} onClick={() => setOverdueOnly(v => !v)} className={`rounded-sm ${overdueOnly ? 'bg-red-600 hover:bg-red-700' : ''}`} data-testid="customers-overdue-filter">Overdue only ({overdueCount})</Button>
              <Button size="sm" variant={sortBal ? 'default' : 'outline'} onClick={() => setSortBal(v => !v)} className="rounded-sm" data-testid="customers-sort-balance">Sort by overdue</Button>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, contact, email…" className="pl-8 rounded-sm" data-testid="customers-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No customers yet</p>
              <p className="text-xs mt-1">Click "Add Customer" to create the first one — needed before you can issue Sales Orders.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email / Phone</TableHead>
                  <TableHead>Billing / Shipping</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-semibold"><Link to={`/customers/${c.id}/history`} className="hover:text-orange-700 hover:underline" data-testid={`customer-history-link-${c.id}`}>{c.customer_name}</Link></TableCell>
                    <TableCell className="text-sm">{c.contact_person || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs">
                      {c.email && <div className="flex items-center gap-1 text-slate-700"><Mail className="w-3 h-3" /> {c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1 text-slate-700 mt-0.5"><Phone className="w-3 h-3" /> {c.phone}</div>}
                      {!c.email && !c.phone && <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs max-w-xs">
                      {c.billing_address && <div className="flex items-start gap-1 text-slate-600"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span className="truncate">B: {c.billing_address}</span></div>}
                      {c.shipping_address && <div className="flex items-start gap-1 text-slate-600 mt-0.5"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /> <span className="truncate">S: {c.shipping_address}</span></div>}
                      {!c.billing_address && !c.shipping_address && <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`customer-balance-${c.id}`}>
                      {bal(c).open_balance > 0 ? <div className="mono font-semibold">${bal(c).open_balance.toFixed(2)}</div> : <span className="text-slate-300">—</span>}
                      {bal(c).overdue_amount > 0 && <span className="inline-block mt-0.5 text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200 px-1.5 py-0.5 rounded-sm" data-testid={`customer-overdue-badge-${c.id}`}>${bal(c).overdue_amount.toFixed(2)} overdue · {bal(c).max_days_overdue}d</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild title="Customer history" data-testid={`customer-history-btn-${c.id}`}><Link to={`/customers/${c.id}/history`}><History className="w-4 h-4" /></Link></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`customer-edit-${c.id}`}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(c)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Customer Name *</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="customer-name-input" className="rounded-sm" />
            </div>
            <div>
              <Label>Contact Person</Label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="customer-contact-input" className="rounded-sm" />
            </div>
            <div>
              <Label>Tax ID</Label>
              <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="customer-tax-input" className="rounded-sm" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" className="rounded-sm" />
            </div>
            <div>
              <Label>Customs Broker Email</Label>
              <Input type="email" value={form.customs_broker_email || ''} onChange={(e) => setForm({ ...form, customs_broker_email: e.target.value })} placeholder="broker@example.com" data-testid="customer-broker-email-input" className="rounded-sm" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" className="rounded-sm" />
            </div>
            <div className="col-span-2">
              <Label>Billing Address</Label>
              <Textarea rows={2} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="customer-billing-input" className="rounded-sm" />
            </div>
            <div className="col-span-2">
              <Label>Shipping Address</Label>
              <Textarea rows={2} value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} data-testid="customer-shipping-input" className="rounded-sm" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-notes-input" className="rounded-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="customer-save-btn">
              {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Customer')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
